/**
 * §S.1 · ¿Está todo en pie?
 *
 * ## Es la sonda profunda, no la de los reinicios
 *
 * Son dos a propósito. `/health` gobierna los reinicios y comprueba lo mínimo; una
 * sonda de reinicio que compruebe demasiado reinicia el proceso porque el proveedor de
 * correo está caído, y convierte el problema pequeño de otro en una caída nuestra.
 * `/health/deep` es la que gobierna la alerta, y es la que se mira aquí.
 *
 * ## El `503` es la respuesta, no un fallo
 *
 * Cuando algo está caído, esta ruta responde `503` **con el cuerpo puesto**: dice qué
 * comprobación falló y por qué. Tratarlo como un error de red pintaría «no se ha
 * podido cargar» justo cuando la pantalla tiene algo que decir — que es la única vez
 * que alguien la abre.
 *
 * Es la misma familia que el `404` de las métricas o el `422` de la comparativa: un
 * código que no es 200 y aun así es la respuesta.
 *
 * ## Se refresca sola, porque se deja abierta
 *
 * Esta pantalla se abre cuando un club llama diciendo que algo va raro y se queda
 * abierta en una pestaña. Una foto fija de hace diez minutos, en ese momento, es peor
 * que no tener pantalla: se lee como si fuera de ahora.
 *
 * ## Lo que comprueba, dicho con palabras
 *
 * `database`, `file_storage` y `scheduled_jobs` son nombres de máquina. Quien mira
 * esto a las ocho de la tarde necesita saber **qué se rompe si eso está rojo**, y eso
 * es lo que se pinta debajo de cada uno.
 */
import { useQuery } from '@tanstack/react-query';
import { BASE } from '@/shared/api/client';
import { Cargando } from '@/shared/ui/Estados';
import { fechaYHora, numero } from '@/shared/ui/formato';

interface Comprobacion {
  readonly ok: boolean;
  readonly ms: number;
  readonly error?: string;
}

interface Estado {
  readonly status: 'ok' | 'degraded';
  readonly checks: Readonly<Record<string, Comprobacion>>;
}

/** Qué es cada comprobación y **qué se rompe** si falla. */
const QUE_ES: Readonly<Record<string, { nombre: string; sin: string }>> = {
  database: {
    nombre: 'La base de datos',
    sin: 'Sin esto no funciona nada: ni entrar, ni pasar lista, ni cobrar.',
  },
  file_storage: {
    nombre: 'El almacén de ficheros',
    sin: 'Sin esto no se suben logos ni certificados, y los diplomas y los recibos no se descargan.',
  },
  scheduled_jobs: {
    nombre: 'Los procesos programados',
    sin: 'Sin esto nada se rompe a la vista: dejan de salir los avisos, las remesas y los recordatorios, y no lo nota nadie hasta que un club pregunta.',
  },
};

const comoSeLlama = (clave: string) =>
  QUE_ES[clave]?.nombre ?? clave.replace(/_/g, ' ');

export function EstadoDelServicio() {
  const estado = useQuery({
    queryKey: ['admin', 'estado-del-servicio'],
    // Cada minuto, y también al volver a la pestaña. Ver la cabecera.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
    queryFn: async () => {
      /*
       * Con `fetch` a pelo y no con el cliente tipado: `/health/deep` es pública, no
       * lleva sesión, y **devuelve `503` con el cuerpo puesto**. El cliente trataría
       * ese código como un error y tiraría justo la respuesta que hay que leer.
       */
      const r = await fetch(new URL('/v1/health/deep', BASE));
      return (await r.json()) as Estado;
    },
  });

  if (estado.isPending) {
    return <Cargando filas={3} etiqueta="Comprobando" />;
  }

  if (estado.isError || !estado.data) {
    /*
     * Aquí sí es un fallo de verdad: ni siquiera se ha podido hablar con el API. Y
     * es la peor noticia posible, así que se dice sin rodeos.
     */
    return (
      <section>
        <h1>Estado</h1>
        <div className="hueco hueco-error">
          <h2>El API no contesta</h2>
          <p>
            Ni siquiera hemos podido preguntarle cómo está.{' '}
            <strong>Eso quiere decir que está caído o no llega la red</strong>,
            y es más grave que cualquier cosa que pudiera decir esta pantalla.
          </p>
          <div className="hueco-acciones">
            <button
              type="button"
              className="btn-primario"
              onClick={() => void estado.refetch()}
            >
              Volver a probar
            </button>
          </div>
        </div>
      </section>
    );
  }

  const datos = estado.data;
  const bien = datos.status === 'ok';
  const entradas = Object.entries(datos.checks);
  const rotas = entradas.filter(([, c]) => !c.ok);

  return (
    <section>
      <header className="titulo-fila">
        <h1>Estado</h1>
        <span className={`pastilla ${bien ? 'pastilla-bien' : 'pastilla-mal'}`}>
          {bien ? 'Todo en pie' : 'Algo está caído'}
        </span>
      </header>

      <p className="tenue">
        La sonda profunda, que es la que dispara la alerta.{' '}
        <strong>La otra</strong> —la que gobierna los reinicios— comprueba menos
        a propósito: una que comprobara esto reiniciaría el servicio porque se
        ha caído el correo.
      </p>

      {!bien && (
        <p className="nota">
          <strong>
            {rotas.length === 1
              ? `Falla ${comoSeLlama(rotas[0]![0]).toLowerCase()}.`
              : `Fallan ${rotas.length} comprobaciones.`}
          </strong>{' '}
          Debajo está el porqué tal cual lo dice el servidor.
        </p>
      )}

      <ul className="papeles">
        {entradas.map(([clave, c]) => {
          const que = QUE_ES[clave];
          return (
            <li key={clave}>
              <div className="aviso-cabeza">
                <strong>{comoSeLlama(clave)}</strong>
                <span
                  className={`pastilla pastilla-left ${
                    c.ok ? 'pastilla-bien' : 'pastilla-mal'
                  }`}
                >
                  {c.ok ? 'Responde' : 'No responde'}
                </span>
                {/*
                  El tiempo también cuando va bien: una base de datos que tarda
                  800 ms en contestar «estoy» no está caída y está a punto.
                */}
                <span className="tenue">{numero(c.ms, 0)} ms</span>
              </div>

              {que && <p className="tenue">{que.sin}</p>}

              {c.error && (
                <p className="texto-mal">
                  {/*
                    La ruta es pública y no cuenta el porqué de un fallo de
                    infraestructura: el detalle va al registro del servidor.
                  */}
                  {c.error === 'unreachable'
                    ? 'No responde. El detalle está en el registro del servidor.'
                    : c.error}
                </p>
              )}
            </li>
          );
        })}
      </ul>

      <p className="tenue">
        Comprobado a las {fechaYHora(new Date())}. Se repite sola cada minuto y
        al volver a esta pestaña.{' '}
        <button
          type="button"
          className="enlace-fila"
          onClick={() => void estado.refetch()}
        >
          Comprobar ahora
        </button>
      </p>
    </section>
  );
}
